/**
 * Performance Clientes Dashboard
 *
 * Client performance overview with:
 * - Client results cards with performance metrics
 * - Uses the same getClientMatrix tRPC backend as client-matrix
 *
 * Styled for Monutrack dark theme with Monumenta Green (#D2FA52) as primary accent.
 */

import { trpc } from '@/api/trpc'
import { useAuth } from '@/hooks/useAuth'
import { useNavigate } from '@tanstack/react-router'
import { Shield, AlertTriangle, Building2, Briefcase, Truck, CreditCard, Globe, TrendingUp, ExternalLink, Crown, Target } from 'lucide-react'
import { useState, useMemo, useCallback } from 'react'
import { usePageTitle } from '@/stores/page-title-store'
import { formatCurrency } from '../../../lib/financial-utils'
import { SensitiveValue } from '@/components/privacy/SensitiveText'
import {
  DREControlsPanel,
  calculatePeriodDates,
  calculateComparisonRange,
  type ComparisonMode,
  type DREGranularity,
  type PeriodDates,
} from '../dre/components'
import { useGetOfficialProa } from '../proa/hooks/useProaData'

// ============================================================================
// TYPES
// ============================================================================

interface ClientAggregation {
  codigoCliente: string
  nomeCliente: string
  receita: number
  custos: number
  margem: number
  margemPercent: number
  notasCount: number
  jobsCount: number
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function getDefaultDateRange(): PeriodDates {
  return calculatePeriodDates('ytd')
}

// Icon mapping for clients
const iconOptions = [Building2, Globe, Truck, Briefcase, CreditCard, TrendingUp]

// Main clients to show individually (others will be aggregated into "Outros")
const MAIN_CLIENTS = [
  'EMBRATUR',
  'FUNCEF',
  'SEBRAE',
  'LTM',
  'BANCO DO BRASIL',
  'APEX',
  'BB CORRETORA',
  'PETROBRAS',
  'PARCERIAS',
  'BB DIGITAL',
]

// Check if a client name matches any main client pattern
function isMainClient(nomeCliente: string): boolean {
  const upperName = nomeCliente.toUpperCase()
  return MAIN_CLIENTS.some(main => upperName.includes(main))
}

// Get margin color based on percentage
function getMarginColor(percent: number): string {
  if (percent >= 30) return 'text-green-400'
  if (percent >= 15) return 'text-brand-primary'
  if (percent >= 0) return 'text-yellow-400'
  return 'text-red-400'
}

function getMarginBgColor(percent: number): string {
  if (percent >= 30) return 'bg-green-500/20 border-green-500/30'
  if (percent >= 15) return 'bg-brand-primary/20 border-brand-primary/30'
  if (percent >= 0) return 'bg-yellow-500/20 border-yellow-500/30'
  return 'bg-red-500/20 border-red-500/30'
}

function getMarginBarColor(percent: number): string {
  if (percent >= 30) return 'bg-green-400'
  if (percent >= 15) return 'bg-brand-primary'
  if (percent >= 0) return 'bg-yellow-400'
  return 'bg-red-400'
}

// Get PROA achievement color based on percentage
function getProaColor(percent: number): string {
  if (percent >= 100) return 'text-green-400'
  if (percent >= 80) return 'text-yellow-400'
  return 'text-red-400'
}

function getProaBarColor(percent: number): string {
  if (percent >= 100) return 'bg-green-400'
  if (percent >= 80) return 'bg-yellow-400'
  return 'bg-red-400'
}

// Skeleton card for loading state
function ClientCardSkeleton() {
  return (
    <div className="border border-brand-gray/20 rounded-lg p-4 bg-brand-dark/30 animate-pulse">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-lg bg-brand-gray/20" />
            <div className="flex-1">
              <div className="h-4 w-32 bg-brand-gray/20 rounded mb-1" />
              <div className="h-3 w-20 bg-brand-gray/10 rounded" />
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <div className="h-3 w-12 bg-brand-gray/10 rounded" />
              <div className="h-5 w-24 bg-brand-gray/20 rounded" />
            </div>
            <div className="flex justify-between">
              <div className="h-3 w-12 bg-brand-gray/10 rounded" />
              <div className="h-4 w-20 bg-brand-gray/20 rounded" />
            </div>
            <div className="flex justify-between">
              <div className="h-3 w-12 bg-brand-gray/10 rounded" />
              <div className="h-4 w-20 bg-brand-gray/20 rounded" />
            </div>
          </div>
        </div>
        <div className="ml-3">
          <div className="w-14 h-14 rounded-full bg-brand-gray/20" />
        </div>
      </div>
    </div>
  )
}


// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function PerformanceClientesDashboard() {
  const { user } = useAuth()
  const navigate = useNavigate()
  usePageTitle('Performance Clientes')
  const defaultDates = getDefaultDateRange()

  // Filter State
  const [startDate, setStartDate] = useState(defaultDates.startDate)
  const [endDate, setEndDate] = useState(defaultDates.endDate)
  const [granularity, setGranularity] = useState<DREGranularity>('monthly')
  const [comparisonMode, setComparisonMode] = useState<ComparisonMode>('none')

  // Calculate comparison date range
  const comparisonRange = useMemo(
    () => calculateComparisonRange(startDate, endDate, comparisonMode),
    [startDate, endDate, comparisonMode]
  )

  // Get the year from startDate for PROA lookup
  const currentYear = parseInt(startDate.split('-')[0])

  // Fetch official PROA for the current year
  const { data: officialProa } = useGetOfficialProa(currentYear)

  // Create a map of client code to PROA annual target
  // Client-level PROA comes from ReceitaBruta metric breakdowns
  const clientProaMap = useMemo(() => {
    const map = new Map<string, number>()
    if (officialProa?.metrics) {
      // Find the ReceitaBruta metric which contains client breakdowns
      const receitaBrutaMetric = officialProa.metrics.find(m => m.lineItem === 'ReceitaBruta')
      if (receitaBrutaMetric?.breakdowns) {
        receitaBrutaMetric.breakdowns.forEach(breakdown => {
          // dimensionId is the client code, annual is the PROA target
          map.set(breakdown.dimensionId, breakdown.annual)
        })
      }
    }
    return map
  }, [officialProa?.metrics])

  // Fetch client matrix data using tRPC (same backend as client-matrix)
  const {
    data: matrixResult,
    isLoading,
    error,
    refetch,
  } = trpc.dre.getClientMatrix.useQuery(
    {
      dataInicial: new Date(startDate),
      dataFinal: new Date(endDate),
    },
    {
      enabled: !!startDate && !!endDate,
      retry: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  )

  // Fetch comparison data
  const { data: comparisonMatrixResult } = trpc.dre.getClientMatrix.useQuery(
    {
      dataInicial: comparisonRange ? new Date(comparisonRange.startDate) : new Date(),
      dataFinal: comparisonRange ? new Date(comparisonRange.endDate) : new Date(),
    },
    {
      enabled: !!comparisonRange,
      retry: false,
      staleTime: 5 * 60 * 1000,
    }
  )

  // Process client data from backend - show main clients separately from others
  const { mainClients, otherClientsList, outrosAggregate } = useMemo(() => {
    if (!matrixResult?.clients) return { mainClients: [], otherClientsList: [], outrosAggregate: null }

    const main: ClientAggregation[] = []
    const others: ClientAggregation[] = []

    // Separate main clients from others
    matrixResult.clients.forEach(c => {
      const client: ClientAggregation = {
        codigoCliente: c.codigoCliente ?? '',
        nomeCliente: c.nomeCliente ?? '',
        receita: c.receita ?? 0,
        custos: c.custos ?? 0,
        margem: c.margem ?? 0,
        margemPercent: c.margemPercent ?? 0,
        notasCount: c.notasCount ?? 0,
        jobsCount: c.jobsCount ?? 0,
      }

      if (isMainClient(client.nomeCliente)) {
        main.push(client)
      } else {
        others.push(client)
      }
    })

    // Sort main clients by receita descending
    main.sort((a, b) => b.receita - a.receita)
    // Sort others by receita descending for insights
    others.sort((a, b) => b.receita - a.receita)

    // Create aggregate for Outros
    let aggregate: ClientAggregation | null = null
    if (others.length > 0) {
      const outrosReceita = others.reduce((sum, c) => sum + c.receita, 0)
      const outrosCustos = others.reduce((sum, c) => sum + c.custos, 0)
      const outrosMargem = others.reduce((sum, c) => sum + c.margem, 0)
      const outrosMargemPercent = outrosReceita > 0 ? (outrosMargem / outrosReceita) * 100 : 0

      aggregate = {
        codigoCliente: 'OUTROS',
        nomeCliente: `Outros`,
        receita: outrosReceita,
        custos: outrosCustos,
        margem: outrosMargem,
        margemPercent: outrosMargemPercent,
        notasCount: others.reduce((sum, c) => sum + c.notasCount, 0),
        jobsCount: others.reduce((sum, c) => sum + c.jobsCount, 0),
      }
    }

    return { mainClients: main, otherClientsList: others, outrosAggregate: aggregate }
  }, [matrixResult])

  // Create comparison map for delta calculations (with aggregated Outros)
  const comparisonMap = useMemo((): Map<string, ClientAggregation> => {
    if (!comparisonRange || !comparisonMatrixResult?.clients) return new Map()
    const map = new Map<string, ClientAggregation>()
    const otherClients: ClientAggregation[] = []

    comparisonMatrixResult.clients.forEach(c => {
      const client: ClientAggregation = {
        codigoCliente: c.codigoCliente ?? '',
        nomeCliente: c.nomeCliente ?? '',
        receita: c.receita ?? 0,
        custos: c.custos ?? 0,
        margem: c.margem ?? 0,
        margemPercent: c.margemPercent ?? 0,
        notasCount: c.notasCount ?? 0,
        jobsCount: c.jobsCount ?? 0,
      }

      if (isMainClient(client.nomeCliente)) {
        map.set(client.codigoCliente, client)
      } else {
        otherClients.push(client)
      }
    })

    // Aggregate others for comparison
    if (otherClients.length > 0) {
      const outrosReceita = otherClients.reduce((sum, c) => sum + c.receita, 0)
      const outrosCustos = otherClients.reduce((sum, c) => sum + c.custos, 0)
      const outrosMargem = otherClients.reduce((sum, c) => sum + c.margem, 0)
      const outrosMargemPercent = outrosReceita > 0 ? (outrosMargem / outrosReceita) * 100 : 0

      map.set('OUTROS', {
        codigoCliente: 'OUTROS',
        nomeCliente: 'Outros',
        receita: outrosReceita,
        custos: outrosCustos,
        margem: outrosMargem,
        margemPercent: outrosMargemPercent,
        notasCount: otherClients.reduce((sum, c) => sum + c.notasCount, 0),
        jobsCount: otherClients.reduce((sum, c) => sum + c.jobsCount, 0),
      })
    }

    return map
  }, [comparisonRange, comparisonMatrixResult])

  // Handlers
  const handlePeriodChange = useCallback((dates: PeriodDates) => {
    setStartDate(dates.startDate)
    setEndDate(dates.endDate)
  }, [])

  const handleRefresh = useCallback(() => {
    void refetch()
  }, [refetch])

  // Navigate to client detail page
  const handleExploreClient = useCallback((clientId: string) => {
    void navigate({
      to: '/financial/client-matrix/$clientId',
      params: { clientId },
      search: { startDate, endDate },
    })
  }, [navigate, startDate, endDate])

  // Access control
  if (!user || (user.role !== 'admin' && user.role !== 'diretor-executivo' && user.role !== 'diretor-financeiro' && user.role !== 'vice-presidente' && user.role !== 'ceo')) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-6 animate-fade-in-up">
        <Shield className="h-16 w-16 text-brand-red animate-pulse" />
        <h1 className="text-3xl font-bold text-white font-display">Acesso Restrito</h1>
        <p className="text-brand-gray-light text-center max-w-md font-body text-lg">
          Esta página contém informações financeiras sensíveis e está disponível apenas para administradores.
        </p>
      </div>
    )
  }

  // Error state (only show full-page error if no data)
  if (error && mainClients.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-brand-red/20 rounded-full flex items-center justify-center mx-auto">
            <AlertTriangle className="h-8 w-8 text-brand-red" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white mb-2">Erro ao Carregar Dados</h2>
            <p className="text-brand-gray-light">{error.message}</p>
            <button
              onClick={handleRefresh}
              className="btn-primary mt-4"
            >
              Tentar Novamente
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Calculate delta for comparison
  const calcDelta = (current: number, comparison: number | undefined): number | null => {
    if (comparison === undefined || comparison === 0) return null
    return ((current - comparison) / Math.abs(comparison)) * 100
  }

  return (
    <div className="space-y-6">
      {/* PROA Badge */}
      {officialProa?.scenario && (
        <div className="flex justify-end">
          <div className="flex items-center gap-2 px-3 py-2 bg-[#D2FA52]/10 border border-[#D2FA52]/30 rounded-lg">
            <Crown className="h-4 w-4 text-[#D2FA52]" />
            <span className="text-sm text-[#D2FA52]">
              PROA: {officialProa.scenario.name}
            </span>
          </div>
        </div>
      )}

      {/* Controls Panel */}
      <DREControlsPanel
        startDate={startDate}
        endDate={endDate}
        onPeriodChange={handlePeriodChange}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onRefresh={handleRefresh}
        isLoading={isLoading}
        comparisonMode={comparisonMode}
        onComparisonModeChange={setComparisonMode}
        granularity={granularity}
        onGranularityChange={setGranularity}
        showGranularity={false}
        showComparison={false}
        showPresets={false}
      />

      {/* Client Results Cards */}
      <div className="bg-brand-light/10 p-6 rounded-lg">
        <h4 className="text-xl font-display font-bold text-white mb-4">
          Performance por Cliente
          {!isLoading && (
            <span className="text-sm font-normal text-brand-gray ml-2">
              ({mainClients.length} principais + {otherClientsList.length} outros)
            </span>
          )}
        </h4>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(9)].map((_, i) => (
              <ClientCardSkeleton key={i} />
            ))}
          </div>
        ) : mainClients.length > 0 ? (
          <>
            {/* Main clients grid - 3 columns */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {mainClients.map((client, index) => {
                const Icon = iconOptions[index % iconOptions.length]
                const proaTarget = clientProaMap.get(client.codigoCliente)
                const proaAchievement = proaTarget && proaTarget > 0 ? (client.receita / proaTarget) * 100 : null
                // For margin bar, cap at 50% for visual purposes (full bar = 50% margin)
                const marginBarPercent = Math.min(Math.max(client.margemPercent, 0), 50) * 2
                // For PROA bar, cap at 120% for visual purposes
                const proaBarPercent = proaAchievement ? Math.min(proaAchievement, 120) * (100/120) : 0

                return (
                  <div
                    key={client.codigoCliente}
                    className="border border-brand-gray/20 rounded-lg p-4 bg-brand-dark/30 hover:bg-brand-dark/40 hover:border-brand-gray/30 transition-all duration-200"
                  >
                    {/* Header with client name and explore button */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="p-2 rounded-lg bg-brand-dark/50">
                          <Icon className="w-5 h-5 text-brand-primary" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="font-semibold text-white text-sm truncate" title={client.nomeCliente}>
                            {client.nomeCliente}
                          </h4>
                        </div>
                      </div>
                      <button
                        onClick={() => handleExploreClient(client.codigoCliente)}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-brand-primary hover:text-white bg-brand-dark/50 hover:bg-brand-primary/20 border border-brand-primary/30 hover:border-brand-primary/50 rounded-lg transition-all duration-200"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Explorar
                      </button>
                    </div>

                    {/* King Metrics - Progress Bars */}
                    <div className="space-y-3 mb-4">
                      {/* MARGEM Progress Bar */}
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-brand-gray uppercase tracking-wide">Margem</span>
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-bold ${getMarginColor(client.margemPercent)}`}>
                              {client.margemPercent.toFixed(0)}%
                            </span>
                            <span className="text-xs text-brand-gray">
                              <SensitiveValue mask="R$ ••••••">
                                {formatCurrency(client.margem)}
                              </SensitiveValue>
                            </span>
                          </div>
                        </div>
                        <div className="h-2 bg-brand-dark/50 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${getMarginBarColor(client.margemPercent)}`}
                            style={{ width: `${marginBarPercent}%` }}
                          />
                        </div>
                      </div>

                      {/* PROA Progress Bar */}
                      {proaTarget !== undefined && proaTarget > 0 ? (
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium text-brand-gray uppercase tracking-wide flex items-center gap-1">
                              <Target className="w-3 h-3" />
                              PROA
                            </span>
                            <div className="flex items-center gap-2">
                              <span className={`text-sm font-bold ${proaAchievement ? getProaColor(proaAchievement) : 'text-brand-gray'}`}>
                                {proaAchievement?.toFixed(0) ?? 0}%
                              </span>
                              <span className="text-xs text-brand-gray">
                                <SensitiveValue mask="R$ ••••••">
                                  {formatCurrency(client.receita)}
                                </SensitiveValue> / <SensitiveValue mask="R$ ••••••">
                                  {formatCurrency(proaTarget)}
                                </SensitiveValue>
                              </span>
                            </div>
                          </div>
                          <div className="h-2 bg-brand-dark/50 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-500 ${proaAchievement ? getProaBarColor(proaAchievement) : 'bg-brand-gray/30'}`}
                              style={{ width: `${proaBarPercent}%` }}
                            />
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-medium text-brand-gray uppercase tracking-wide flex items-center gap-1">
                              <Target className="w-3 h-3" />
                              PROA
                            </span>
                            <span className="text-xs text-brand-gray/50 italic">Sem meta definida</span>
                          </div>
                          <div className="h-2 bg-brand-dark/50 rounded-full overflow-hidden">
                            <div className="h-full rounded-full bg-brand-gray/20" style={{ width: '0%' }} />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Supporting Data */}
                    <div className="border-t border-brand-gray/10 pt-3 grid grid-cols-2 gap-3">
                      <div>
                        <span className="text-xs text-brand-gray block">Receita</span>
                        <span className="text-sm font-semibold text-brand-primary">
                          <SensitiveValue mask="R$ ••••••">
                            {formatCurrency(client.receita)}
                          </SensitiveValue>
                        </span>
                      </div>
                      <div>
                        <span className="text-xs text-brand-gray block">Custos</span>
                        <span className="text-sm font-semibold text-red-400">
                          <SensitiveValue mask="R$ ••••••">
                            {formatCurrency(client.custos)}
                          </SensitiveValue>
                        </span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Outros - Special expanded card */}
            {outrosAggregate && otherClientsList.length > 0 && (
              <div className="mt-6">
                <div className={`border rounded-lg p-5 ${getMarginBgColor(outrosAggregate.margemPercent)}`}>
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-brand-dark/50">
                        <Building2 className="w-5 h-5 text-brand-gray" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-white">
                          Outros Clientes
                          <span className="text-sm font-normal text-brand-gray ml-2">
                            ({otherClientsList.length} clientes)
                          </span>
                        </h4>
                      </div>
                    </div>

                    {/* Totals */}
                    <div className="text-right">
                      <p className="text-lg font-bold text-brand-primary">
                        <SensitiveValue mask="R$ ••••••">
                          {formatCurrency(outrosAggregate.receita)}
                        </SensitiveValue>
                      </p>
                      <p className={`text-sm font-medium ${outrosAggregate.margem >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        Margem: {outrosAggregate.margemPercent.toFixed(0)}%
                      </p>
                    </div>
                  </div>

                  {/* Top clients breakdown */}
                  <div className="border-t border-brand-gray/20 pt-4">
                    <p className="text-xs text-brand-gray mb-3 uppercase tracking-wide">Top clientes no grupo</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {otherClientsList.slice(0, 6).map((client, idx) => (
                        <div
                          key={client.codigoCliente}
                          className="flex items-center justify-between bg-brand-dark/30 rounded px-3 py-2"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-xs text-brand-gray w-4">{idx + 1}.</span>
                            <span className="text-sm text-white truncate" title={client.nomeCliente}>
                              {client.nomeCliente}
                            </span>
                          </div>
                          <span className="text-sm font-medium text-brand-primary ml-2 whitespace-nowrap">
                            <SensitiveValue mask="R$ ••••••">
                              {formatCurrency(client.receita)}
                            </SensitiveValue>
                          </span>
                        </div>
                      ))}
                    </div>
                    {otherClientsList.length > 6 && (
                      <p className="text-xs text-brand-gray mt-3 text-center">
                        + {otherClientsList.length - 6} outros clientes
                      </p>
                    )}
                  </div>

                  {/* Summary stats */}
                  <div className="border-t border-brand-gray/20 pt-4 mt-4 grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="text-xs text-brand-gray mb-1">Receita Total</p>
                      <p className="text-sm font-bold text-brand-primary">
                        <SensitiveValue mask="R$ ••••••">
                          {formatCurrency(outrosAggregate.receita)}
                        </SensitiveValue>
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-brand-gray mb-1">Custos Total</p>
                      <p className="text-sm font-bold text-red-400">
                        <SensitiveValue mask="R$ ••••••">
                          {formatCurrency(outrosAggregate.custos)}
                        </SensitiveValue>
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-brand-gray mb-1">Margem Total</p>
                      <p className={`text-sm font-bold ${outrosAggregate.margem >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        <SensitiveValue mask="R$ ••••••">
                          {formatCurrency(outrosAggregate.margem)}
                        </SensitiveValue>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-brand-gray">Nenhum cliente encontrado para o período selecionado.</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default PerformanceClientesDashboard
